import { useMemo } from 'react';

/**
 * Hook for query autocomplete suggestions based on cursor position
 * 
 * CRITICAL CONSTRAINTS:
 * - Context guessing is HEURISTIC ONLY - best-effort UX helper, not a parser
 * - Must NEVER throw - always return valid suggestions array
 * - Must tolerate garbage text - handle malformed, incomplete, or invalid input
 * - Must degrade to empty suggestions when unsure
 * - Must be O(n) over prefix - linear scan, no recursion
 * - Must be simple - no grammar rules, no tokenization, just pattern matching
 * - NO backend parsing calls - only schema fetch allowed
 */

/**
 * Context states for autocomplete
 */
const CONTEXT_STATE = {
  FIELD: 'FIELD',       // Suggesting field names
  OPERATOR: 'OPERATOR', // Suggesting operators for a field
  VALUE: 'VALUE',       // Suggesting values for a field
  LOGICAL: 'LOGICAL',   // Suggesting logical operators (AND, OR, NOT)
  NONE: 'NONE'          // No suggestions or ambiguous context
};

/**
 * Heuristically guess the context at cursor position
 * 
 * This is a simple linear scan - NOT a grammar parser.
 * It looks for patterns like:
 * - "field " -> OPERATOR context
 * - "field =" -> VALUE context
 * - "field = value" -> NONE (complete expression)
 * - "" or " " -> FIELD context
 * 
 * @param {string} text - Full query text
 * @param {number} cursorPosition - Cursor position in text
 * @returns {{state: string, field?: string, operator?: string, prefix: string}} - Context object
 */
function guessContext(text, cursorPosition) {
  try {
    const prefixRaw = text && typeof text === 'string' 
      ? text.substring(0, Math.max(0, Math.min(cursorPosition, text.length)))
      : '';

    if (!text || typeof text !== 'string') {
      return { state: CONTEXT_STATE.FIELD, prefix: prefixRaw };
    }

    if (cursorPosition < 0 || cursorPosition > text.length) {
      return { state: CONTEXT_STATE.NONE, prefix: prefixRaw };
    }

    const prefix = prefixRaw.trim();

    if (prefix.length === 0) {
      return { state: CONTEXT_STATE.FIELD, prefix: prefixRaw };
    }
    
    // Check for incomplete quoted value FIRST — must take priority over logical pattern
    // so that typing "Verify and" inside quotes doesn't trigger FIELD suggestions
    const incompleteQuotedValuePattern = /(\w+)\s+(>=|<=|!=|=|>|<|contains|startsWith|endsWith)\s+(["'])([^"']*)$/i;
    const incompleteQuotedMatch = prefixRaw.match(incompleteQuotedValuePattern);
    if (incompleteQuotedMatch) {
      return {
        state: CONTEXT_STATE.VALUE,
        field: incompleteQuotedMatch[1],
        operator: incompleteQuotedMatch[2],
        prefix: prefixRaw
      };
    }

    // Only check for logical keywords AFTER confirming we're not inside a quoted value
    const logicalPattern = /\b(AND|OR|NOT)(\s+)?$/i;
    if (logicalPattern.test(prefixRaw)) {
      return { state: CONTEXT_STATE.FIELD, prefix: prefixRaw };
    }
    
    const incompleteBracketValuePattern = /(\w+)\s+(in|notIn)\s+\[([^\]]*)$/i;
    const incompleteBracketMatch = prefixRaw.match(incompleteBracketValuePattern);
    if (incompleteBracketMatch) {
      const bracketContent = incompleteBracketMatch[3];
      const isEmptyBracket = bracketContent.trim().length === 0;
      const hasComma = bracketContent.includes(',');
      
      let hasIncompleteQuote = false;
      if (bracketContent.length > 0) {
        const doubleQuoteIndex = bracketContent.indexOf('"');
        if (doubleQuoteIndex >= 0) {
          const afterQuote = bracketContent.substring(doubleQuoteIndex + 1);
          const closingQuoteIndex = afterQuote.indexOf('"');
          if (closingQuoteIndex < 0) {
            hasIncompleteQuote = true;
          }
        }
        if (!hasIncompleteQuote) {
          const singleQuoteIndex = bracketContent.indexOf("'");
          if (singleQuoteIndex >= 0) {
            const afterQuote = bracketContent.substring(singleQuoteIndex + 1);
            const closingQuoteIndex = afterQuote.indexOf("'");
            if (closingQuoteIndex < 0) {
              hasIncompleteQuote = true;
            }
          }
        }
      }
      
      if (isEmptyBracket || hasComma || hasIncompleteQuote) {
        return {
          state: CONTEXT_STATE.VALUE,
          field: incompleteBracketMatch[1],
          operator: incompleteBracketMatch[2],
          prefix: prefixRaw
        };
      }
    }
    
    const valuePattern = /(\w+)\s+(>=|<=|!=|=|>|<|contains|startsWith|endsWith|notIn|in|isNotEmpty|isEmpty)(\s*)$/i;
    const valueMatch = prefixRaw.match(valuePattern);
    if (valueMatch) {
      return {
        state: CONTEXT_STATE.VALUE,
        field: valueMatch[1],
        operator: valueMatch[2],
        prefix: prefixRaw
      };
    }

    const operatorPattern = /(\w+)\s+$/;
    const operatorMatch = prefixRaw.match(operatorPattern);
    if (operatorMatch) {
      const word = operatorMatch[1].toUpperCase();
      if (word !== 'AND' && word !== 'OR' && word !== 'NOT') {
      return {
        state: CONTEXT_STATE.OPERATOR,
          field: operatorMatch[1],
        prefix: prefixRaw
      };
      }
    }

    if (!prefixRaw.endsWith(' ')) {
      const wordPattern = /\b(\w+)$/;
      const wordMatch = prefixRaw.match(wordPattern);
      if (wordMatch) {
        const word = wordMatch[1].toUpperCase();
        const beforeCurrentWord = prefixRaw.substring(0, wordMatch.index).trim();
        
        if (word === 'AND' || word === 'OR' || word === 'NOT') {
          return { state: CONTEXT_STATE.FIELD, prefix: prefixRaw };
        }
        
        const operatorBeforeValuePattern = /(\w+)\s+(>=|<=|!=|=|>|<|contains|startsWith|endsWith|notIn|in|isNotEmpty|isEmpty)\s+(\w+)$/i;
        const operatorBeforeValueMatch = prefixRaw.match(operatorBeforeValuePattern);
        if (operatorBeforeValueMatch) {
          return {
            state: CONTEXT_STATE.VALUE,
            field: operatorBeforeValueMatch[1],
            operator: operatorBeforeValueMatch[2],
            prefix: prefixRaw
          };
        }
        
        const fieldBeforeOpPattern = /^(\w+)\s+(\w+)$/;
        const fieldBeforeOpMatch = prefixRaw.match(fieldBeforeOpPattern);
        if (fieldBeforeOpMatch) {
          const firstWord = fieldBeforeOpMatch[1].toUpperCase();
          if (firstWord !== 'AND' && firstWord !== 'OR' && firstWord !== 'NOT') {
            return {
              state: CONTEXT_STATE.OPERATOR,
              field: fieldBeforeOpMatch[1],
              prefix: prefixRaw
            };
          }
        }
        
        const logicalBeforeFieldPattern = /\b(AND|OR|NOT)\s+(\w+)$/i;
        if (logicalBeforeFieldPattern.test(prefixRaw)) {
          return { state: CONTEXT_STATE.FIELD, prefix: prefixRaw };
        }
        
        const predicateBeforeLogicalPattern = /(\w+)\s+(>=|<=|!=|=|>|<|contains|startsWith|endsWith|in|notIn|isNotEmpty|isEmpty)\s+((?:"[^"]*"|'[^']*'|\[[^\]]*\]|\d+))\s+(\w+)$/i;
        const predicateBeforeLogicalMatch = prefixRaw.match(predicateBeforeLogicalPattern);
        if (predicateBeforeLogicalMatch) {
          return { state: CONTEXT_STATE.LOGICAL, prefix: prefixRaw };
        }
        
        if (beforeCurrentWord === '' || /^\s+$/.test(beforeCurrentWord)) {
        return { state: CONTEXT_STATE.FIELD, prefix: prefixRaw };
      }
      }
    }

    // Pattern 5: After complete predicate -> LOGICAL context
    // Match complete predicates like:
    // - "status = \"New\"" (quoted string)
    // - "status = \"New\" " (quoted string with trailing space)
    // - "priority in [\"High\"]" (bracket list)
    // - "title isEmpty" (unary operator)
    // - "count > 10" (number)
    
    // Check for unary operators first (isEmpty, isNotEmpty)
    const unaryOpPattern = /(\w+)\s+(isEmpty|isNotEmpty)(\s*)$/i;
    if (unaryOpPattern.test(prefixRaw)) {
      return { state: CONTEXT_STATE.LOGICAL, prefix: prefixRaw };
    }
    
    // Check for binary operators with complete values
    // Pattern: field + operator + value (quoted, bracketed, or number)
    const binaryOpPattern = /(\w+)\s+(>=|<=|!=|=|>|<|contains|startsWith|endsWith|in|notIn)\s+((?:"[^"]*"|'[^']*'|\[[^\]]*\]|\d+)\s*)$/i;
    const binaryMatch = prefixRaw.match(binaryOpPattern);
    if (binaryMatch) {
      const valuePart = binaryMatch[3].trim();
      // Value is complete if:
      // - It's a quoted string (starts and ends with quote)
      // - It's a bracket list (starts with [ and ends with ])
      // - It's a number followed by space
      const isCompleteValue = 
        (/^["']/.test(valuePart) && /["']$/.test(valuePart)) ||  // Complete quoted string
        (/^\[/.test(valuePart) && /\]$/.test(valuePart)) ||     // Complete bracket list
        (/^\d+$/.test(valuePart) && prefixRaw.endsWith(' '));   // Number with trailing space
      
      if (isCompleteValue) {
        return { state: CONTEXT_STATE.LOGICAL, prefix: prefixRaw };
      }
    }
    
    // Pattern 6: After closing paren -> LOGICAL context
    if (prefixRaw.trim().endsWith(')')) {
      return { state: CONTEXT_STATE.LOGICAL, prefix: prefixRaw };
    }

    // Default: ambiguous or incomplete expression -> no suggestions
    return { state: CONTEXT_STATE.NONE, prefix: prefixRaw };
  } catch (error) {
    // NEVER throw - degrade gracefully
    console.warn('Error in guessContext:', error);
    const prefixRaw = text && typeof text === 'string' 
      ? text.substring(0, Math.max(0, Math.min(cursorPosition, text.length)))
      : '';
    return { state: CONTEXT_STATE.NONE, prefix: prefixRaw };
  }
}

/**
 * Extract the token being typed at cursor position
 * This helps filter suggestions based on what user is currently typing
 * 
 * @param {string} prefixRaw - Raw prefix before cursor
 * @param {string} contextState - Current context state
 * @returns {string} - Lowercase token being typed
 */
function extractTypingToken(prefixRaw, contextState) {
  try {
    if (!prefixRaw) return '';
    
    if (contextState === CONTEXT_STATE.FIELD) {
      const match = prefixRaw.match(/(?:^|AND\s+|OR\s+|NOT\s+)(\w*)$/i);
      return match ? match[1].toLowerCase() : '';
    }
    
    if (contextState === CONTEXT_STATE.OPERATOR) {
      const match = prefixRaw.match(/\w+\s+(\w*)$/);
      return match ? match[1].toLowerCase() : '';
    }
    
    if (contextState === CONTEXT_STATE.VALUE) {
      const quotedMatch = prefixRaw.match(/(?:>=|<=|!=|=|>|<|contains|startsWith|endsWith)\s+(["'])([^"']*)$/i);
      if (quotedMatch) {
        return quotedMatch[2].toLowerCase();
      }
      
      const bracketMatch = prefixRaw.match(/(?:in|notIn)\s+\[([^\]]*)$/i);
      if (bracketMatch) {
        const bracketContent = bracketMatch[1];
        const lastCommaIndex = bracketContent.lastIndexOf(',');
        if (lastCommaIndex >= 0) {
          const afterComma = bracketContent.substring(lastCommaIndex + 1).trim();
          if (/^["']/.test(afterComma)) {
            const quotedMatch = afterComma.match(/^["']([^"']*)$/);
            return quotedMatch ? quotedMatch[1].toLowerCase() : '';
          }
          return afterComma.toLowerCase();
        } else {
          const firstValue = bracketContent.trim();
          if (/^["']/.test(firstValue)) {
            const quotedMatch = firstValue.match(/^["']([^"']*)$/);
            return quotedMatch ? quotedMatch[1].toLowerCase() : '';
          }
          return firstValue.toLowerCase();
        }
      }
      
      const match = prefixRaw.match(/(?:>=|<=|!=|=|>|<|contains|startsWith|endsWith|notIn|in|isNotEmpty|isEmpty)\s*(\w*)$/i);
      return match ? match[1].toLowerCase() : '';
    }
    
    if (contextState === CONTEXT_STATE.LOGICAL) {
      const match = prefixRaw.match(/(\w*)$/);
      const word = match ? match[1].toUpperCase() : '';
      if (word === 'A' || word === 'AN' || word === 'AND') {
        return 'and';
      }
      if (word === 'O' || word === 'OR') {
        return 'or';
      }
      if (word === 'N' || word === 'NO' || word === 'NOT') {
        return 'not';
      }
      return '';
    }
    
    return '';
  } catch (error) {
    console.warn('Error extracting typing token:', error);
    return '';
  }
}

/**
 * Convert label (with underscores) or field name to canonical field name
 * 
 * @param {string} fieldText - Field text (could be label like "Created_By" or field name like "generated_by_user")
 * @param {object} schema - Query schema
 * @returns {string} Canonical field name
 */
function normalizeFieldName(fieldText, schema) {
  if (!fieldText || !schema?.fields) return fieldText;
  
  const fieldTextLower = fieldText.toLowerCase();
  
  const directMatch = schema.fields.find(f => f.name.toLowerCase() === fieldTextLower);
  if (directMatch) {
    return directMatch.name;
  }
  
  const labelMatch = schema.fields.find(f => {
    if (!f.label) return false;
    const labelKey = f.label.replace(/\s+/g, '_').toLowerCase();
    return labelKey === fieldTextLower || f.label.toLowerCase() === fieldTextLower;
  });
  
  if (labelMatch) {
    return labelMatch.name;
  }
  
  return fieldText;
}

/**
 * Generate suggestions based on context and schema
 * 
 * @param {object} context - Context object from guessContext
 * @param {object} schema - Schema from Redux store
 * @returns {Array<{type: string, value: string, label: string, description?: string}>}
 */
function generateSuggestions(context, schema) {
  try {
    if (!schema || !schema.fields || !Array.isArray(schema.fields)) {
      return [];
    }

    const { state, field, operator, prefix } = context;
    const suggestions = [];

    if (state === CONTEXT_STATE.FIELD) {
      // Extract what user is currently typing
      const typingToken = extractTypingToken(prefix, state);
      
      // Filter fields by what user is typing (match both field name and label)
      const matchingFields = schema.fields.filter(f => {
        if (!typingToken) return true;
        
        const tokenLower = typingToken.toLowerCase();
        const fieldNameLower = f.name.toLowerCase();
        const label = f.label || f.name;
        const labelLower = label.toLowerCase();
        const labelKey = label.replace(/\s+/g, '_').toLowerCase();
        
        return fieldNameLower.startsWith(tokenLower) ||
               labelLower.startsWith(tokenLower) ||
               labelKey.startsWith(tokenLower);
      });
      
      matchingFields.forEach(fieldDef => {
          const label = fieldDef.label || fieldDef.name;
          const labelKey = label.replace(/\s+/g, '_').toLowerCase();
          
          suggestions.push({
            type: 'field',
            value: labelKey,
            label: label,
            description: fieldDef.description
          });
        });
      
    } else if (state === CONTEXT_STATE.OPERATOR && field) {
      const normalizedFieldName = normalizeFieldName(field, schema);
      
      const fieldDef = schema.fields.find(
        f => f.name.toLowerCase() === normalizedFieldName.toLowerCase()
      );
      
      if (fieldDef && fieldDef.operators && Array.isArray(fieldDef.operators)) {
        const typingToken = extractTypingToken(prefix, state);
        
        const operatorLabels = {
          '=': 'Equals',
          '!=': 'Not equals',
          '>': 'Greater than',
          '<': 'Less than',
          '>=': 'Greater than or equal',
          '<=': 'Less than or equal',
          'contains': 'Contains',
          'startsWith': 'Starts with',
          'endsWith': 'Ends with',
          'in': 'In',
          'notIn': 'Not in',
          'isEmpty': 'Is empty',
          'isNotEmpty': 'Is not empty'
        };

        const matchingOps = fieldDef.operators.filter(op => 
          !typingToken || op.toLowerCase().startsWith(typingToken)
        );

        matchingOps.forEach(op => {
            suggestions.push({
              type: 'operator',
              value: op,
              label: operatorLabels[op] || op
            });
          });
      }
      
    } else if (state === CONTEXT_STATE.VALUE && field && operator) {
      const normalizedFieldName = normalizeFieldName(field, schema);
      
      const fieldDef = schema.fields.find(
        f => f.name.toLowerCase() === normalizedFieldName.toLowerCase()
      );
      
      if (fieldDef) {
        if (fieldDef.kind === 'enum' && fieldDef.values && Array.isArray(fieldDef.values)) {
          const typingToken = extractTypingToken(prefix, state);
          
          const matchingValues = fieldDef.values.filter(v => {
            if (!typingToken) return true;
            
            if (typeof v === 'object' && v !== null) {
              if (v.label) {
                return String(v.label).toLowerCase().startsWith(typingToken.toLowerCase());
              }
              if (v.username) {
                return String(v.username).toLowerCase().startsWith(typingToken.toLowerCase());
              }
              const stringValue = Object.values(v).find(val => typeof val === 'string');
              return stringValue ? stringValue.toLowerCase().startsWith(typingToken.toLowerCase()) : false;
            }
            
            return String(v).toLowerCase().startsWith(typingToken.toLowerCase());
          });
          
          matchingValues.forEach(value => {
            let suggestionValue = '';
            let suggestionLabel = '';
            
            if (typeof value === 'object' && value !== null) {
              if (value.id && value.username) {
                suggestionValue = value.username;
                suggestionLabel = value.label || value.username;
              } else if (value.label) {
                suggestionValue = value.label;
                suggestionLabel = value.label;
              } else {
                const stringProp = Object.values(value).find(val => typeof val === 'string');
                suggestionValue = stringProp || String(value);
                suggestionLabel = stringProp || String(value);
              }
            } else {
              suggestionValue = String(value);
              suggestionLabel = String(value);
            }
            
            suggestions.push({
              type: 'value',
              value: suggestionValue,
              label: suggestionLabel
            });
          });
        }
      }
    } else if (state === CONTEXT_STATE.LOGICAL) {
      const typingToken = extractTypingToken(prefix, state);
      
      const logicalOperators = [
        { value: 'AND', label: 'AND' },
        { value: 'OR', label: 'OR' },
        { value: 'NOT', label: 'NOT' }
      ];
      
      const matchingOps = logicalOperators.filter(op => 
        !typingToken || op.value.toLowerCase().startsWith(typingToken)
      );
      
      matchingOps.forEach(op => {
        suggestions.push({
          type: 'logical',
          value: op.value,
          label: op.label
        });
      });
    }

    return suggestions;
  } catch (error) {
    // NEVER throw - return empty suggestions
    console.warn('Error in generateSuggestions:', error);
    return [];
  }
}

/**
 * useQueryAutocomplete hook
 * 
 * @param {string} text - Query text
 * @param {number} cursorPosition - Cursor position in text
 * @param {string} entity - Entity type (default: 'testcase')
 * @returns {{suggestions: Array, loading: boolean, error: object|null, context: object}}
 */
/**
 * Hook for query autocomplete suggestions
 * 
 * @param {string} text - Query text
 * @param {number} cursorPosition - Cursor position in text
 * @param {string} entity - Entity type (default: 'testcase')
 * @param {Object|null} schemaOverride - Query schema (REQUIRED - no longer fetches from Redux)
 * @returns {{suggestions: Array, loading: boolean, error: object|null, context: object}}
 */
export function useQueryAutocomplete(text = '', cursorPosition = 0, entity = 'testcase', schemaOverride = null) {
  // Schema must be provided via schemaOverride prop (single source of truth from TestCaseList)
  // No longer fetches from Redux to avoid stale state issues
  const schema = schemaOverride;

  // Compute context and suggestions using useMemo for performance
  const { context, suggestions } = useMemo(() => {
    // Always return valid values, never throw
    try {
      const ctx = guessContext(text, cursorPosition);
      const suggs = generateSuggestions(ctx, schema);
      return { context: ctx, suggestions: suggs };
    } catch (error) {
      console.warn('Error in useQueryAutocomplete useMemo:', error);
      return {
        context: { state: CONTEXT_STATE.NONE, prefix: '' },
        suggestions: []
      };
    }
  }, [text, cursorPosition, schema]);

  return {
    suggestions,
    loading: false,
    error: null,
    context
  };
}

export { CONTEXT_STATE };